import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { Package, Booking, Admin, SpecialPackage, TestimonialFormData, Testimonial, GalleryItem } from '../types';

const API_URL = process.env.REACT_APP_API_URL;

// Create axios instance
const api: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests if available
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: (email: string, password: string): Promise<AxiosResponse<{ success: boolean; token: string; admin: Admin }>> =>
    api.post('/auth/login', { email, password }),
  getMe: (): Promise<AxiosResponse<{ success: boolean; admin: Admin }>> =>
    api.get('/auth/me'),
};

// Package API (One-day packages)
export const packageAPI = {
  getAll: (): Promise<AxiosResponse<{ success: boolean; packages: Package[] }>> =>
    api.get('/packages'),
  getAllAdmin: (): Promise<AxiosResponse<{ success: boolean; packages: Package[] }>> =>
    api.get('/packages/all'),
  getOne: (id: string): Promise<AxiosResponse<{ success: boolean; package: Package }>> =>
    api.get(`/packages/${id}`),
  create: (data: Partial<Package>): Promise<AxiosResponse<{ success: boolean; package: Package }>> =>
    api.post('/packages', data),
  update: (id: string, data: Partial<Package>): Promise<AxiosResponse<{ success: boolean; package: Package }>> =>
    api.put(`/packages/${id}`, data),
  toggle: (id: string): Promise<AxiosResponse<{ success: boolean; package: Package }>> =>
    api.put(`/packages/${id}/toggle`),
  delete: (id: string): Promise<AxiosResponse<{ success: boolean }>> =>
    api.delete(`/packages/${id}`),
};

// Special Package API (Multi-day packages)
export const specialPackageAPI = {
  getAll: (): Promise<AxiosResponse<{ success: boolean; packages: SpecialPackage[] }>> =>
    api.get('/special-packages'),
  getAllAdmin: (): Promise<AxiosResponse<{ success: boolean; packages: SpecialPackage[] }>> =>
    api.get('/special-packages/all'),
  getOne: (id: string): Promise<AxiosResponse<{ success: boolean; package: SpecialPackage }>> =>
    api.get(`/special-packages/${id}`),
  create: (data: Partial<SpecialPackage>): Promise<AxiosResponse<{ success: boolean; package: SpecialPackage }>> =>
    api.post('/special-packages', data),
  update: (id: string, data: Partial<SpecialPackage>): Promise<AxiosResponse<{ success: boolean; package: SpecialPackage }>> =>
    api.put(`/special-packages/${id}`, data),
  toggle: (id: string): Promise<AxiosResponse<{ success: boolean; package: SpecialPackage }>> =>
    api.put(`/special-packages/${id}/toggle`),
  delete: (id: string): Promise<AxiosResponse<{ success: boolean }>> =>
    api.delete(`/special-packages/${id}`),
};

// Overlap detection interfaces
export interface OverlappingBooking {
  id: string;
  packageName: string;
  customerName: string;
  startDate: string;
  endDate: string;
  numberOfPeople: number;
  phone: string;
  email: string;
}

export interface OverlapCheckResponse {
  success: boolean;
  hasOverlap: boolean;
  overlapCount: number;
  overlappingBookings: OverlappingBooking[];
}

export interface BookingWithOverlap {
  success: boolean;
  booking: Booking;
  overlap: {
    hasOverlap: boolean;
    count: number;
    bookings: OverlappingBooking[];
  };
}

// Booking API
export const bookingAPI = {
  getAll: (): Promise<AxiosResponse<{ success: boolean; bookings: Booking[] }>> =>
    api.get('/bookings'),
  getCalendar: (): Promise<AxiosResponse<{ success: boolean; bookings: Booking[] }>> =>
    api.get('/bookings/calendar'),
  getOne: (id: string): Promise<AxiosResponse<BookingWithOverlap>> =>
    api.get(`/bookings/${id}`),
  create: (data: Partial<Booking>): Promise<AxiosResponse<{ success: boolean; booking: Booking }>> =>
    api.post('/bookings', data),
  accept: (id: string, forceAccept: boolean = false): Promise<AxiosResponse<{ success: boolean; booking: Booking; message: string }>> =>
    api.put(`/bookings/${id}/accept`, { forceAccept }),
  cancel: (id: string): Promise<AxiosResponse<{ success: boolean; booking: Booking }>> =>
    api.put(`/bookings/${id}/cancel`),
  delete: (id: string): Promise<AxiosResponse<{ success: boolean }>> =>
    api.delete(`/bookings/${id}`),
  
  // Overlap detection APIs
  checkOverlap: (id: string): Promise<AxiosResponse<OverlapCheckResponse>> =>
    api.get(`/bookings/check-overlap/${id}`),
  checkDates: (startDate: string, endDate: string, excludeBookingId?: string): Promise<AxiosResponse<OverlapCheckResponse>> =>
    api.post('/bookings/check-dates', {
      startDate,
      endDate,
      excludeBookingId,
    }),
};

export const testimonialAPI = {
  // Get all approved testimonials (public)
  getAll: (): Promise<AxiosResponse<{ success: boolean; testimonials: Testimonial[] }>> =>
    api.get('/testimonials'),
  
  // Get all testimonials including pending (admin)
  getAllAdmin: (): Promise<AxiosResponse<{ success: boolean; testimonials: Testimonial[] }>> =>
    api.get('/testimonials/all'),
  
  // Get single testimonial
  getOne: (id: string): Promise<AxiosResponse<{ success: boolean; testimonial: Testimonial }>> =>
    api.get(`/testimonials/${id}`),
  
  // Submit new testimonial (public)
  create: (data: TestimonialFormData): Promise<AxiosResponse<{ success: boolean; message: string; testimonial: Testimonial }>> =>
    api.post('/testimonials', data),
  
  // Approve testimonial (admin)
  approve: (id: string): Promise<AxiosResponse<{ success: boolean; message: string; testimonial: Testimonial }>> =>
    api.put(`/testimonials/${id}/approve`),
  
  // Reject testimonial (admin)
  reject: (id: string): Promise<AxiosResponse<{ success: boolean; message: string; testimonial: Testimonial }>> =>
    api.put(`/testimonials/${id}/reject`),
  
  // Delete testimonial (admin)
  delete: (id: string): Promise<AxiosResponse<{ success: boolean; message: string }>> =>
    api.delete(`/testimonials/${id}`),
  
  // Get statistics (admin)
  getStats: (): Promise<AxiosResponse<{ success: boolean; stats: { total: number; approved: number; pending: number; rejected: number; avgRating: number } }>> =>
    api.get('/testimonials/stats/summary'),
};

export const galleryAPI = {
  // Get all active gallery items (public)
  getAll: (): Promise<AxiosResponse<{ success: boolean; items: GalleryItem[] }>> =>
    api.get('/gallery'),
  
  // Get all gallery items including hidden (admin)
  getAllAdmin: (): Promise<AxiosResponse<{ success: boolean; items: GalleryItem[] }>> =>
    api.get('/gallery/all'),
  
  // Get single gallery item
  getOne: (id: string): Promise<AxiosResponse<{ success: boolean; item: GalleryItem }>> =>
    api.get(`/gallery/${id}`),
  
  // Create new gallery item (admin)
  create: (data: Partial<GalleryItem>): Promise<AxiosResponse<{ success: boolean; message: string; item: GalleryItem }>> =>
    api.post('/gallery', data),
  
  // Update gallery item (admin)
  update: (id: string, data: Partial<GalleryItem>): Promise<AxiosResponse<{ success: boolean; message: string; item: GalleryItem }>> =>
    api.put(`/gallery/${id}`, data),
  
  // Toggle gallery item visibility (admin)
  toggle: (id: string): Promise<AxiosResponse<{ success: boolean; message: string; item: GalleryItem }>> =>
    api.put(`/gallery/${id}/toggle`),
  
  // Delete gallery item (admin)
  delete: (id: string): Promise<AxiosResponse<{ success: boolean; message: string }>> =>
    api.delete(`/gallery/${id}`),
};

export default api;
